import type { PropsWithChildren } from 'react';
import { createContext, useMemo } from 'react';
import { camelCase, upperFirst } from 'es-toolkit';
import type { StorageDriver } from './types';
import { defineDriver } from './utils/define-driver';

interface StorageProviderProps extends PropsWithChildren {
  widgetId: string;
  name: string;
}

export interface IStorageContext {
  name: string;
  storageDriver?: StorageDriver;
  widgetId?: string | number;
}

export const storageContext = createContext<IStorageContext>({
  name: '',
  storageDriver: undefined,
  widgetId: undefined
});

export function StorageProvider({
  children,
  widgetId,
  name
}: StorageProviderProps) {
  if (!name) {
    throw new Error(`Name is required for ${StorageProvider.name}`);
  }

  const memoProviderValue = useMemo(
    () => ({
      widgetId,
      name: pascalCase(name),
      storageDriver: defineDriver()
    }),
    [widgetId, name]
  );

  return (
    <storageContext.Provider value={memoProviderValue}>
      {children}
    </storageContext.Provider>
  );
}

export function withPrefix(key: string, prefix?: string | number): string {
  return prefix ? `${prefix}.${key}` : key;
}

export function withPostfix(key: string, postfix?: string | number): string {
  return postfix ? `${key}.${postfix}` : key;
}

export function pascalCase(str: string): string {
  return upperFirst(camelCase(str));
}
